import { OnDestroy, Pipe, PipeTransform } from '@angular/core'
import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core'
import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { filter, tap } from 'rxjs/operators'

import { FormlySelectFieldProps } from '../components/formly/types/select-field-type/select-field-type.component'

export interface FormlySelectOption<T = unknown> {
  label: string
  disabled?: boolean
  hidden?: boolean
  value?: string
  data?: T
  group?: FormlySelectOption<T>[]
}

@Pipe({ name: 'localFormlySelectOptions' })
export class FormlySelectOptionsPipe<T> implements PipeTransform, OnDestroy {
  private _subscription!: Subscription | null
  private _options!: BehaviorSubject<FormlySelectOption<T>[]> | null

  public transform(
    options: FormlySelectOption<T>[] | Observable<FormlySelectOption<T>[]>,
    field: FieldTypeConfig<FormlySelectFieldProps<T>>
  ): Observable<FormlySelectOption<T>[]> {
    if (options instanceof Observable) {
      this.dispose()
    } else {
      options = this.observableOf(options, field)
    }
    return options
  }

  public ngOnDestroy(): void {
    this.dispose()
  }

  private dispose(): void {
    if (this._options) {
      this._options.complete()
      this._options = null
    }

    if (this._subscription) {
      this._subscription.unsubscribe()
      this._subscription = null
    }
  }

  private observableOf(
    options: FormlySelectOption<T>[],
    fieldConfig: FormlyFieldConfig<FormlySelectFieldProps<T>>
  ): Observable<FormlySelectOption<T>[]> {
    this.dispose()
    if (fieldConfig.options?.fieldChanges) {
      this._subscription = fieldConfig.options.fieldChanges
        .pipe(
          filter(
            ({ property, type, field }) =>
              type === 'expressionChanges' &&
              property.indexOf('props.options') === 0 &&
              field === fieldConfig &&
              Array.isArray(field.props?.options) &&
              !!this._options
          ),
          tap(() => {
            this._options?.next(
              fieldConfig.props?.options as FormlySelectOption<T>[]
            )
          })
        )
        .subscribe()
    }

    this._options = new BehaviorSubject(options)
    return this._options.asObservable()
  }
}
